/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import moment from 'moment';

import { LONG_DATE_ONLY_DISPLAY, formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import { Button, FullscreenDialog, Grid, Typography, Typography2, breakpoints } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';

import _t from 'i18n!nls/degree-home';

import EnrolledIcon from './images/enrolled-icon.svg';
import type { EnrollingCourse } from './types/EnrollingCourse';

type EnrollmentConfirmationViewProps = {
  onClose: () => void;
  enrollments: EnrollingCourse[];
};

const styles = {
  container: () => css`
    padding-top: var(--cds-spacing-1400);
    overflow-y: auto;

    ${breakpoints.down('sm')} {
      padding-top: var(--cds-spacing-400);
    }
  `,
  icon: () => css`
    width: 70px;
    margin-bottom: var(--cds-spacing-400);
  `,
  pageTitle: () => css`
    margin-bottom: var(--cds-spacing-100);
  `,
  enrollmentList: () => css`
    margin-top: var(--cds-spacing-400);
  `,
  enrollmentItem: () => css`
    border-bottom: 1px solid var(--cds-color-grey-25);
    padding: var(--cds-spacing-200) 0;

    &:last-of-type {
      border-bottom: 0;
    }
  `,
  startBtn: () => css`
    margin-right: var(--cds-spacing-100);
  `,
};

const EnrollmentConfirmationHeading = () => (
  <>
    <img css={styles.icon} src={EnrolledIcon} alt={_t(`You're enrolled`)} />
    {/* eslint-disable-next-line @coursera-web/custom-rules/prefer-cds-typography2 -- FIXME: legacy; migrate to Typography2 */}
    <Typography component="h1" variant="h1semibold" css={styles.pageTitle}>
      {_t(`Now that you're enrolled, start learning!`)}
    </Typography>
  </>
);
const SingleEnrollmentConfirmationView = ({ onClose, enrollments }: EnrollmentConfirmationViewProps) => {
  const { Content, Actions } = FullscreenDialog;
  // @ts-expect-error TS2339 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  const { name, session, slug } = enrollments[0];

  return (
    <>
      <Content id="enrollmentDialogContent" aria-live="polite" aria-atomic="true">
        <div css={styles.container}>
          <EnrollmentConfirmationHeading />
          <Typography2 component="p" variant="bodyPrimary">
            {_t('#{name} will run from #{startDate} to #{endDate}.', {
              name,
              startDate: formatDateTimeDisplay(session.startsAt, LONG_DATE_ONLY_DISPLAY),
              endDate: formatDateTimeDisplay(session.endsAt, LONG_DATE_ONLY_DISPLAY),
            })}
          </Typography2>
        </div>
      </Content>
      <Actions>
        {moment()?.isAfter(session.startsAt) && (
          <Button component="a" css={styles.startBtn} href={`/learn/${slug}/home`}>
            {_t('Start now')}
          </Button>
        )}

        <Button variant="secondary" onClick={onClose}>
          {_t('Go back home')}
        </Button>
      </Actions>
    </>
  );
};

const MultipleEnrollmentConfirmationView = ({ onClose, enrollments }: EnrollmentConfirmationViewProps) => {
  const { Content, Actions } = FullscreenDialog;
  return (
    <>
      <Content id="enrollmentDialogContent" css={styles.container} aria-live="polite" aria-atomic="true">
        <EnrollmentConfirmationHeading />
        <div css={styles.enrollmentList}>
          {enrollments.map(({ name, session, id, slug }) => (
            <Grid container css={styles.enrollmentItem} alignItems="center" key={`enrollment-item-${id}`}>
              <Grid item xs>
                <Typography2 component="p" variant="bodyPrimary">
                  <strong>{name}</strong>
                </Typography2>
                <Typography2 component="p" variant="bodySecondary">
                  {_t(`Runs from #{startDate} to #{endDate}`, {
                    startDate: formatDateTimeDisplay(session.startsAt, LONG_DATE_ONLY_DISPLAY),
                    endDate: formatDateTimeDisplay(session.endsAt, LONG_DATE_ONLY_DISPLAY),
                  })}
                </Typography2>
              </Grid>
              {moment()?.isAfter(session.startsAt) && (
                <Grid item>
                  <Button
                    size="small"
                    variant="ghost"
                    icon={<ArrowNextIcon />}
                    component="a"
                    href={`/learn/${slug}/home`}
                  >
                    {_t(`Start now`)}
                  </Button>
                </Grid>
              )}
            </Grid>
          ))}
        </div>
      </Content>
      <Actions>
        <Button variant="secondary" onClick={onClose}>
          {_t('Go back home')}
        </Button>
      </Actions>
    </>
  );
};

export const EnrollmentConfirmationView = (props: EnrollmentConfirmationViewProps) => (
  <>
    {props.enrollments.length > 1 ? (
      <MultipleEnrollmentConfirmationView {...props} />
    ) : (
      <SingleEnrollmentConfirmationView {...props} />
    )}
  </>
);

export default EnrollmentConfirmationView;
